import React from "react";
import { getTimeAgo } from "utils/dateUtils";
import { useFlightStatus } from "hooks/useFlightStatus";
import LiveFlightStatus from "./LiveFlightStatus";

interface AnsweredFlightDetailsProps {
  /** The load-request row (typed loosely to match the parent list). */
  flight: any;
}

// Drive the single flight-status badge from the live flightState (falling back
// to "Scheduled" until the backend publishes one). Keeps the established green
// "Scheduled" look while reflecting real states like Landed / Cancelled.
const badgeFor = (state?: string): { text: string; className: string } => {
  switch ((state || "").toLowerCase()) {
    case "active":
    case "en-route":
    case "enroute":
      return { text: state as string, className: "bg-blue-500" };
    case "landed":
      return { text: state as string, className: "bg-gray-500" };
    case "delayed":
    case "diverted":
      return { text: state as string, className: "bg-orange-500" };
    case "cancelled":
    case "canceled":
    case "incident":
      return { text: state as string, className: "bg-red-500" };
    case "scheduled":
    case "":
    case undefined as any:
      return { text: "Scheduled", className: "bg-green-500" };
    default:
      return { text: state as string, className: "bg-green-500" };
  }
};

/**
 * Body of the "Answered" accordion: responder + open seats + live flight status.
 * Owns the Firestore listener for the row (via useFlightStatus) so the header
 * badge and the live panel below share a single subscription.
 */
const AnsweredFlightDetails: React.FC<AnsweredFlightDetailsProps> = ({
  flight,
}) => {
  const enabled = flight?.isEnabledStatus === true;
  const { status } = useFlightStatus(flight?.id, enabled);

  const badge = badgeFor(status?.flightState);

  return (
    <div className="bg-white rounded-b-lg mt-3">
      <div className="p-4 border-t border-gray-300">
        <div className="grid grid-cols-12 gap-3 sm:gap-6 items-end justify-between">
          <div className="flex flex-col gap-1 lg:col-span-2 md:col-span-8 col-span-12">
            <p className="text-lightgray-200 text-xs">Answered By</p>
            <p className="text-grease-100 text-sm font-medium">
              {flight?.responderName || "Unknown"}
            </p>
            <div className="flex items-center gap-1 text-xs text-lightgray-150">
              <i className="st-time"></i>
              {/* When the request was ANSWERED — use answeredDate (the freshest
                  LoadResponse timestamp), NOT the request's createdDate/
                  modifiedDate which stay frozen at request-creation time and so
                  don't reflect re-answers. Falls back to modifiedDate only if
                  answeredDate is absent. */}
              <p>
                {getTimeAgo(flight?.answeredDate ?? flight?.modifiedDate)}
              </p>
            </div>
          </div>
          <div className="flex md:flex-nowrap flex-wrap items-center lg:col-span-5 lg:justify-center col-span-12 gap-3 sm:gap-6">
            <div className="flex flex-col gap-2 w-full md:w-auto">
              <p className="text-lightgray-200 text-xs">Open Seats</p>
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <div className="flex flex-col items-center border border-lightgray-100 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-sm font-medium">
                    {flight?.firstClass || 0}
                  </p>
                  <p className="text-lightgray-150 text-xs">First</p>
                </div>
                <div className="flex flex-col items-center border border-lightgray-100 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-sm font-medium">
                    {flight?.busClass || 0}
                  </p>
                  <p className="text-lightgray-150 text-xs">Bus</p>
                </div>
                <div className="flex flex-col items-center border border-lightgray-100 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-sm font-medium">
                    {flight?.ecoPlus || 0}
                  </p>
                  <p className="text-lightgray-150 text-xs">Eco+</p>
                </div>
                <div className="flex flex-col items-center border border-lightgray-100 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-sm font-medium">
                    {flight?.eco || 0}
                  </p>
                  <p className="text-lightgray-150 text-xs">Eco</p>
                </div>
                <div className="flex flex-col items-center bg-lightgray-100 bg-opacity-40 py-1 flex-1 min-w-[52px] sm:w-12 sm:flex-none rounded-md">
                  <p className="text-grease-100 text-sm font-medium">
                    {flight?.total || 0}
                  </p>
                  <p className="text-lightgray-150 text-xs">Total</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-lightgray-200 text-xs">
                Listed Non-Rev Passengers
              </p>
              <div className="flex flex-col items-center bg-lightgray-100 bg-opacity-40 py-1 w-full rounded-md">
                <p className="text-grease-100 text-sm font-medium">
                  {flight?.nonRevListed || 0}
                </p>
                <p className="text-lightgray-150 text-xs">Total</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 md:col-span-12 col-span-12 gap-3">
            <div className="flex items-center justify-between gap-4 border p-3 border-lightgray-100 rounded-md">
              <p className="text-grease-100 text-sm font-medium">
                {flight?.flightNo}
              </p>
              <p
                className={`text-white ${badge.className} rounded-sm px-1 py-0.5 text-xs uppercase`}
              >
                {badge.text}
              </p>
            </div>
          </div>
        </div>

        {/* Live flight status — subscribes only while this accordion is open
            AND the user enabled status updates. Renders nothing until the
            backend publishes a doc, so the schedule above stays the fallback. */}
        {enabled && <LiveFlightStatus status={status} />}
      </div>
    </div>
  );
};

export default AnsweredFlightDetails;
